import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Users, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ClientSwitcher = ({ onClientChange, currentClient }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    checkUserRole();
    fetchClients();
  }, []);

  const checkUserRole = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(user.role || '');
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/whitelabel/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setClients(response.data.clients || []);
        
        // If no client selected and we have clients, select first one
        if (!currentClient && response.data.clients.length > 0) {
          onClientChange(response.data.clients[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load clients');
      }
    } finally {
      setLoading(false);
    }
  };

  // Only show for white label users
  if (userRole !== 'agency_admin' && userRole !== 'AGENCY_ADMIN') {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Users className="w-5 h-5 animate-pulse" />
        <span className="text-sm">Loading clients...</span>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 border border-orange-200 rounded-lg">
        <Building2 className="w-5 h-5 text-orange-600" />
        <span className="text-sm text-orange-700">No clients added yet</span>
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => window.location.href = '/settings'}
          className="ml-2"
        >
          Add Client
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-blue-600" />
        <span className="text-sm font-medium text-gray-700">View Client:</span>
      </div>
      <Select value={currentClient || ''} onValueChange={onClientChange}>
        <SelectTrigger className="w-[250px]">
          <SelectValue placeholder="Select a client" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span>All Clients</span>
            </div>
          </SelectItem>
          {clients.map((client) => (
            <SelectItem key={client.id} value={client.id}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${client.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <span>{client.name}</span>
                {client.brand_count > 0 && (
                  <span className="text-xs text-gray-500">({client.brand_count} brands)</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default ClientSwitcher;
